import React, { FC } from 'react';
import Image from 'next/image';
import { Box, Center, HStack, Text } from '@chakra-ui/react';
import { Vault } from '@app/hooks/plugins/useVaults';
import { ExternalDocumentLink } from '@app/components/ExternalDocumentLink';
import { useChainExplorer } from '@app/hooks/other/useExplorerAddress';
import { formatUSD } from '@app/helpers/formatUSD';
import { NitroDetails } from '@app/screens/liquidity/AddLiquidityV3/components/NitroDetails';
import { Address } from '@thirdweb-dev/sdk';
import { Currency } from '@cryptoalgebra/kim-sdk';

interface Props {
  selectedVault: Vault | undefined;
  hasStrategy: boolean;
  nftPoolAddress: string | undefined | null;
  provider: 'steer' | 'ichi' | null;
  poolAddress: Address;
  currencyA: Currency;
  currencyB: Currency;
}

export const ProviderStrategyDetails: FC<Props> = ({
  poolAddress,
  currencyB,
  currencyA,
  selectedVault,
  hasStrategy,
  provider,
  nftPoolAddress
}) => {
  const { explorer } = useChainExplorer();

  function renderInfoRow(label: string, value: string) {
    return (
      <HStack justifyContent="space-between" mb={3}>
        <Text fontSize="16px" fontWeight={400} lineHeight="26px" color="gray.4">
          {label}
        </Text>
        <Text fontSize="16px" fontWeight={500} lineHeight="26px" color="gray.4">
          {value}
        </Text>
      </HStack>
    );
  }

  if (!hasStrategy) {
    return (
      <Center w="100%" minHeight="400px" bg="neutral.800">
        <Text fontSize={16} color="neutral.400">
          Select strategy
        </Text>
      </Center>
    );
  }

  return (
    <Box w="100%">
      <HStack
        bg="gray.2"
        borderRadius={4}
        w="100%"
        justifyContent="space-between"
        p={4}
        minWidth="100%"
      >
        <Image
          src={getProviderImage(provider)}
          height={30}
          width={100}
          alt="Strategy"
        />
      </HStack>
      {selectedVault && (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            paddingBottom: '52%',
            my: 4
          }}
        >
          <Image src={selectedVault.image} fill alt="Strategy" />
        </Box>
      )}

      <HStack my={3}>
        <Text color="orange" fontWeight={400} fontSize={18}>
          {selectedVault?.name}
        </Text>
      </HStack>
      <Text color="neutral.400" fontSize={16}>
        {selectedVault?.strategyDescription}
      </Text>
      <HStack mt={3} width="100%" justifyContent="flex-end">
        <ExternalDocumentLink
          text="Contract"
          href={`${explorer?.url}/address/${selectedVault?.pool}`}
          showAsButton
        />
        <ExternalDocumentLink
          text="Details"
          href={selectedVault?.link ?? ''}
          showAsButton
        />
      </HStack>

      <Box mt={8}>
        {renderInfoRow('Fee APR', `${selectedVault?.apr ?? 0}%`)}
        {renderInfoRow(
          'TVL',
          `${selectedVault?.tvl ? formatUSD.format(selectedVault.tvl) : ''}`
        )}
      </Box>

      {nftPoolAddress && (
        <NitroDetails
          poolAddress={poolAddress}
          currencyA={currencyA}
          currencyB={currencyB}
          nftPoolAddress={nftPoolAddress}
        />
      )}
    </Box>
  );
};

function getProviderImage(provider: string | null) {
  if (!provider) {
    return '';
  }

  switch (provider) {
    case 'steer': {
      return '/assets/images/providers/steer.svg';
    }
    default: {
      return '';
    }
  }
}
